import { useCallback, useEffect, useState } from 'react'
import {
  Badge,
  Box,
  Circle,
  HStack,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react'
import { MapPin } from 'lucide-react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { taskService, type FeVisitEvent } from '@/lib/services/taskService'
import { useToastNotification } from '@/lib/utils/useToastNotification'

type Props = {
  taskId: number
}

const EVENT_LABELS: Record<string, string> = {
  accept: 'Visit accepted',
  accepted: 'Visit accepted',
  reject: 'Visit rejected',
  rejected: 'Visit rejected',
  eta_submitted: 'ETA submitted',
  in_transit: 'En route',
  en_route: 'En route',
  arrival: 'Arrived on site',
  arrived: 'Arrived on site',
  start_job: 'Job started',
  in_progress: 'Job in progress',
  end_job: 'Job completed',
  completed: 'Job completed',
  reschedule: 'Rescheduled',
  closed: 'Visit closed',
  issue_with_documents: 'Issue reported with documents',
  fe_visit_pending: 'Pending acceptance',
  fe_visit_accepted: 'Visit accepted',
  fe_visit_rejected: 'Visit rejected',
  fe_visit_eta_submitted: 'ETA submitted',
  fe_visit_en_route: 'En route',
  fe_visit_arrived: 'Arrived on site',
  fe_visit_in_progress: 'Job in progress',
  fe_visit_resolved: 'Visit resolved',
  fe_visit_revisit: 'Revisit scheduled',
  fe_visit_issue_with_documents: 'Issue reported',
  fe_visit_closed: 'Visit closed',
  location_ping: 'Location update',
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('en-PH', {
    timeZone: 'Asia/Manila',
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })
}

function eventLabel(eventType: string): string {
  const normalized = eventType.trim().toLowerCase()
  if (EVENT_LABELS[normalized]) return EVENT_LABELS[normalized]
  return normalized.replace(/^fe_visit_/, '').replace(/_/g, ' ')
}

function eventBadgeColor(eventType: string): string {
  const normalized = eventType.trim().toLowerCase()
  if (normalized.includes('reject') || normalized.includes('issue')) return 'red'
  if (normalized.includes('closed') || normalized.includes('completed') || normalized.includes('resolved')) {
    return 'green'
  }
  if (normalized.includes('transit') || normalized.includes('en_route') || normalized.includes('arriv')) {
    return 'blue'
  }
  if (normalized.includes('pending')) return 'yellow'
  return 'gray'
}

function payloadDetail(payload: Record<string, unknown> | null | undefined): string | null {
  if (!payload || typeof payload !== 'object') return null
  const message = payload.message ?? payload.summary ?? payload.note ?? payload.reason
  return typeof message === 'string' && message.trim() ? message.trim() : null
}

function formatCoordinates(lat?: number | null, lng?: number | null): string | null {
  if (lat == null || lng == null) return null
  return `${lat.toFixed(5)}, ${lng.toFixed(5)}`
}

export default function FeVisitTimeline({ taskId }: Props) {
  const colors = useThemeColors()
  const { showError } = useToastNotification()
  const [events, setEvents] = useState<FeVisitEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [endpointMissing, setEndpointMissing] = useState(false)

  const loadEvents = useCallback(async () => {
    setLoading(true)
    try {
      const result = await taskService.listVisitEvents(taskId)
      setEvents(result.events)
      setEndpointMissing(result.endpointMissing)
    } catch (err) {
      showError(err as Error)
    } finally {
      setLoading(false)
    }
  }, [taskId, showError])

  useEffect(() => {
    void loadEvents()
  }, [loadEvents])

  if (loading) {
    return (
      <HStack py={6} justify="center">
        <Spinner size="sm" />
        <Text fontSize="sm" color={colors.textSecondary}>
          Loading visit events…
        </Text>
      </HStack>
    )
  }

  if (events.length === 0) {
    return (
      <VStack align="stretch" spacing={2} py={4}>
        <Text fontSize="sm" color={colors.textSecondary}>
          {endpointMissing
            ? 'Visit event tracking is not available yet for this environment.'
            : 'No field visit events recorded yet.'}
        </Text>
      </VStack>
    )
  }

  return (
    <VStack align="stretch" spacing={0} position="relative" pl={6}>
      <Box
        position="absolute"
        left="9px"
        top="8px"
        bottom="8px"
        width="2px"
        bg={colors.borderLight}
        borderRadius="full"
      />
      {events.map((event, index) => {
        const detail = payloadDetail(event.payload)
        const coords = formatCoordinates(event.latitude, event.longitude)
        const isLast = index === events.length - 1

        return (
          <Box key={event.id} position="relative" pb={isLast ? 0 : 4}>
            <Circle
              size="10px"
              position="absolute"
              left="-21px"
              top="6px"
              bg={`${eventBadgeColor(event.event_type)}.400`}
              border="2px solid"
              borderColor={colors.cardBg}
            />
            <VStack align="stretch" spacing={1}>
              <HStack justify="space-between" align="flex-start" flexWrap="wrap" gap={2}>
                <HStack spacing={2} flexWrap="wrap">
                  <Badge colorScheme={eventBadgeColor(event.event_type)} textTransform="capitalize">
                    {eventLabel(event.event_type)}
                  </Badge>
                  {event.actor_name ? (
                    <Text fontSize="sm" fontWeight="600">
                      {event.actor_name}
                    </Text>
                  ) : null}
                </HStack>
                <Text fontSize="xs" color={colors.textSecondary} whiteSpace="nowrap">
                  {formatDate(event.occurred_at)}
                </Text>
              </HStack>
              {detail ? (
                <Text fontSize="sm" whiteSpace="pre-wrap">
                  {detail}
                </Text>
              ) : null}
              {coords ? (
                <HStack spacing={1} color={colors.textSecondary}>
                  <MapPin size={12} />
                  <Text fontSize="xs">{coords}</Text>
                </HStack>
              ) : null}
            </VStack>
          </Box>
        )
      })}
    </VStack>
  )
}
